import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, Upload, X, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const cle = (p) => `${p.source}:${p.ref}`;

const Groupe = ({ titre, items, value, onToggle }) => items.length > 0 && (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{titre}</p>
    <div className="space-y-1.5">
      {items.map((p) => (
        <label key={cle(p)} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`pj-option-${p.source}-${p.ref}`}>
          <Checkbox checked={value.some((v) => cle(v) === cle(p))} onCheckedChange={() => onToggle(p)} />
          <FileText className="h-3.5 w-3.5 text-slate-400" /> {p.nom}
        </label>
      ))}
    </div>
  </div>
);

export const PiecesJointesPicker = ({ prospectId, candidatId, value, onChange }) => {
  const [docs, setDocs] = useState({ devis: [], centre: [] });
  const [envoi, setEnvoi] = useState(false);
  const input = useRef(null);

  useEffect(() => {
    const params = prospectId ? { prospect_id: prospectId } : { candidat_id: candidatId };
    axios.get('/emails-envoyes/documents', { params }).then((r) => setDocs(r.data)).catch(() => {});
  }, [prospectId, candidatId]);

  const toggle = (p) => onChange(value.some((v) => cle(v) === cle(p)) ? value.filter((v) => cle(v) !== cle(p)) : [...value, p]);

  const televerser = async (e) => {
    const fichiers = [...(e.target.files || [])];
    e.target.value = '';
    setEnvoi(true);
    const ajoutes = [];
    for (const f of fichiers) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} dépasse 10 Mo`); continue; }
      const fd = new FormData();
      fd.append('file', f);
      try { ajoutes.push((await axios.post('/emails-envoyes/pieces', fd)).data); }
      catch (err) { toast.error(err.response?.data?.detail || `Échec de l'ajout de ${f.name}`); }
    }
    onChange([...value, ...ajoutes]);
    setEnvoi(false);
  };

  const uploads = value.filter((v) => v.source === 'upload');
  return (
    <div className="space-y-3 p-4 rounded-lg border bg-white" data-testid="pj-picker">
      <p className="flex items-center gap-2 text-sm font-medium text-slate-700"><Paperclip className="h-4 w-4" /> Pièces jointes {value.length > 0 && `(${value.length})`}</p>
      <Groupe titre="Devis" items={docs.devis} value={value} onToggle={toggle} />
      <Groupe titre="Documents du centre" items={docs.centre} value={value} onToggle={toggle} />
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Fichiers de mon ordinateur</p>
        {uploads.map((p) => (
          <div key={p.ref} className="flex items-center justify-between text-sm py-0.5" data-testid={`pj-upload-${p.ref}`}>
            <span className="truncate"><FileText className="inline h-3.5 w-3.5 mr-1 text-slate-400" />{p.nom}</span>
            <button type="button" onClick={() => toggle(p)} className="text-slate-400 hover:text-red-500" data-testid={`pj-retirer-${p.ref}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
        <input ref={input} type="file" multiple className="hidden" onChange={televerser} data-testid="pj-input-fichier" />
        <Button type="button" variant="outline" size="sm" className="mt-1" disabled={envoi} onClick={() => input.current?.click()} data-testid="pj-ajouter-fichier">
          {envoi ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Ajouter un fichier (10 Mo max)
        </Button>
      </div>
    </div>
  );
};
